import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { flushSync } from 'react-dom';

import './App.css';
import ApiKeyModal from './ApiKeyModal';
import Header from './header';
import ModelOutputPanel from './ModelOutputPanel';
import Settings from './Settings';
import { AnthropicModel, streamAnthropicResponse } from './api/anthropicClient';
import { Source } from './types';

const DEFAULT_TEXT = 'Model response will appear here...';
const ADD_API_KEY_OPTION = '__add_api_key__';

interface Model {
  name: string;
  description: string;
  remote_hosted: boolean;
}

interface Models {
  [key: string]: Model;
}

interface PreparedPrompt {
  prompt: string;
  sources: Source[];
  return_all_sources: boolean;
}

axios.defaults.withCredentials = true;

function App() {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [loading, setLoading] = useState(false);
  const [model, setModel] = useState<string>('');
  const [useRag, setUseRag] = useState(false);
  const [availableModels, setAvailableModels] = useState<Models>({});
  const [anthropicModels, setAnthropicModels] = useState<Record<string, AnthropicModel>>({});
  const [anthropicApiKey, setAnthropicApiKey] = useState<string | null>(null);
  const [sources, setSources] = useState<Source[]>([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState(false);
  const [numDocs, setNumDocs] = useState<number | undefined>(undefined);
  const [returnAllSources, setReturnAllSources] = useState<boolean>(false);
  const [streaming, setStreaming] = useState<boolean>(false);

  const apiUrl = process.env.REACT_APP_API_URL || '';

  const selectedAnthropicModel = anthropicModels[model];

  const isModelRemote = useMemo(() => {
    if (selectedAnthropicModel) return true;
    return Boolean(availableModels[model]?.remote_hosted);
  }, [availableModels, model, selectedAnthropicModel]);

  const fetchModels = async () => {
    try {
      const response = await axios.get<{ models: Models }>(`${apiUrl}/models`);
      setAvailableModels(response.data.models);
      const defaultModel = isLoggedIn ? 'gemma-31b-gcp' : 'gemma';

      if (response.data.models[defaultModel]) {
        setModel(defaultModel);
      } else {
        const modelIds = Object.keys(response.data.models);
        if (modelIds.length > 0) {
          setModel(modelIds[0]);
        }
      }
      setUseRag(isLoggedIn);
    } catch (error) {
      console.error('Error fetching models:', error);
    }
  };

  const buildPromptRequestBody = (questionText: string, useRagFlag: boolean) => ({
    question: questionText,
    use_rag: useRagFlag,
    ...(useRagFlag && numDocs !== undefined && { num_docs: numDocs }),
    ...(isLoggedIn && { return_all_sources: returnAllSources }),
  });

  const streamBackendResponse = async (
    questionText: string,
    modelId: string,
    useRagFlag: boolean,
    onUpdate: (answer: string, sources: Source[]) => void,
    onError: (error: string) => void,
    onComplete: () => void,
  ) => {
    try {
      const response = await fetch(`${apiUrl}/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...buildPromptRequestBody(questionText, useRagFlag), model: modelId }),
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body reader available');

      const decoder = new TextDecoder();
      let acc = '';
      let lastSources: Source[] = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        for (const line of chunk.split('\n').filter((l) => l.trim())) {
          try {
            const data = JSON.parse(line);

            if (data.error) {
              onError(data.error);
              return;
            }

            if (data.answer !== undefined || data.sources !== undefined) {
              if (data.answer !== undefined) acc += data.answer;
              lastSources = data.sources || lastSources;
              onUpdate(acc, lastSources);
            }

            if (data.done) {
              onComplete();
              return;
            }
          } catch (parseError) {
            console.error('Error parsing JSON chunk:', parseError, 'Chunk:', line);
          }
        }
      }
    } catch (error) {
      console.error('Streaming error:', error);
      onError(error instanceof Error ? error.message : 'Unknown error occurred');
    }
  };

  const submitToAnthropic = async (
    questionText: string,
    modelId: string,
    useRagFlag: boolean,
    onUpdate: (answer: string, sources: Source[]) => void,
    onError: (error: string) => void,
    onComplete: () => void,
  ) => {
    if (!anthropicApiKey) {
      onError('Add your Anthropic API key to use this model.');
      return;
    }

    try {
      const preparedResponse = await fetch(`${apiUrl}/prepare-prompt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(buildPromptRequestBody(questionText, useRagFlag)),
      });
      if (!preparedResponse.ok) {
        throw new Error(`Failed to prepare prompt: HTTP ${preparedResponse.status}`);
      }
      const prepared: PreparedPrompt = await preparedResponse.json();

      let acc = '';
      for await (const delta of streamAnthropicResponse(prepared.prompt, modelId, anthropicApiKey)) {
        acc += delta;
        onUpdate(acc, filterCitedSources(prepared.sources, acc, prepared.return_all_sources));
      }
      onUpdate(acc, filterCitedSources(prepared.sources, acc, prepared.return_all_sources));
      onComplete();
    } catch (error) {
      console.error('Anthropic streaming error:', sanitizeForLogging(error));
      onError(error instanceof Error ? error.message : 'Unknown error occurred');
    }
  };

  const checkLoginStatus = async () => {
    try {
      const response = await axios.get(`${apiUrl}/check-auth`);
      setIsLoggedIn(response.data.status === 'authenticated');
    } catch (error) {
      console.error('Error checking auth status:', error);
      setIsLoggedIn(false);
    }
  };

  useEffect(() => {
    checkLoginStatus();
  }, []);

  useEffect(() => {
    fetchModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAnswer('');
    setSources([]);

    if (!model) {
      setLoading(false);
      setStreaming(false);
      return;
    }

    setLoading(true);
    setStreaming(true);

    const onUpdate = (a: string, s: Source[]) => {
      setLoading(false);
      flushSync(() => {
        setAnswer(a);
        setSources(s);
      });
    };
    const onError = (err: string) => {
      setAnswer(`Error occurred while fetching ${model} response: ${err}`);
      setSources([]);
      setLoading(false);
      setStreaming(false);
    };
    const onComplete = () => {
      setLoading(false);
      setStreaming(false);
    };

    if (selectedAnthropicModel) {
      await submitToAnthropic(question, model, useRag, onUpdate, onError, onComplete);
    } else {
      await streamBackendResponse(question, model, useRag, onUpdate, onError, onComplete);
    }
  };

  const handleLoginClick = () => {
    if (!isLoggedIn) {
      setShowPasswordModal(true);
    } else {
      setShowSettingsModal(true);
    }
  };

  const handleLoginSubmit = async () => {
    try {
      const response = await axios.post(`${apiUrl}/login`, { password });
      if (response.data.status === 'success') {
        setShowPasswordModal(false);
        setPassword('');
        setLoginError(false);
        setTimeout(checkLoginStatus, 100);
      }
    } catch (error) {
      console.error('Login failed:', error);
      setLoginError(true);
    }
  };

  const handleModalClose = () => {
    setShowPasswordModal(false);
    setPassword('');
    setLoginError(false);
  };

  const handleModalClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) handleModalClose();
  };

  const handleSettingsClose = () => setShowSettingsModal(false);

  const handleSettingsSave = (settings: { numDocs: number | undefined; returnAllSources: boolean }) => {
    setNumDocs(settings.numDocs);
    setReturnAllSources(settings.returnAllSources);
    setShowSettingsModal(false);
  };

  const handleSettingsLogout = async () => {
    try {
      await axios.post(`${apiUrl}/logout`);
      setShowSettingsModal(false);
      setNumDocs(undefined);
      setReturnAllSources(false);
      clearAnthropicState();
      await checkLoginStatus();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const clearAnthropicState = () => {
    if (anthropicModels[model]) setModel('');
    setAnthropicApiKey(null);
    setAnthropicModels({});
  };

  const handleModelSelection = (value: string) => {
    if (value === ADD_API_KEY_OPTION) {
      setShowApiKeyModal(true);
      return;
    }
    setModel(value);
  };

  const handleApiKeyComplete = ({
    apiKey,
    models,
    selectedModelId,
  }: {
    apiKey: string;
    models: AnthropicModel[];
    selectedModelId: string;
  }) => {
    setAnthropicApiKey(apiKey);
    setAnthropicModels(Object.fromEntries(models.map((m) => [m.id, m])));
    setModel(selectedModelId);
    setShowApiKeyModal(false);
  };

  return (
    <div className="container">
      <Header isLoggedIn={isLoggedIn} onClickLogin={handleLoginClick} />

      {showPasswordModal && (
        <div className="modal-overlay" onClick={handleModalClick}>
          <div className="modal-content">
            <h2>Enter Password</h2>
            {loginError ? (
              <>
                <div className="error-message">Incorrect password. Please try again.</div>
                <div className="modal-buttons">
                  <button className="modal-button cancel" onClick={handleModalClose}>
                    Close
                  </button>
                </div>
              </>
            ) : (
              <>
                <input
                  type="password"
                  placeholder="Enter password"
                  className="modal-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleLoginSubmit();
                  }}
                />
                <div className="modal-buttons">
                  <button className="modal-button cancel" onClick={handleModalClose}>
                    Cancel
                  </button>
                  <button className="modal-button submit" onClick={handleLoginSubmit}>
                    Submit
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showSettingsModal && (
        <Settings
          show={showSettingsModal}
          onClose={handleSettingsClose}
          onSave={handleSettingsSave}
          onLogout={handleSettingsLogout}
          initialNumDocs={numDocs}
          initialReturnAllSources={returnAllSources}
          isLoggedIn={isLoggedIn}
        />
      )}

      {showApiKeyModal && (
        <ApiKeyModal
          onCancel={() => setShowApiKeyModal(false)}
          onComplete={handleApiKeyComplete}
        />
      )}

      <div className="outputs">
        <div className="input-column">
          <form className="input-form" onSubmit={handleSubmit}>
            <div className="input-wrapper">
              <textarea
                className="large-input"
                value={question}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setQuestion(e.target.value)}
                placeholder="Ask a research question..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
              />
            </div>
            <div style={{ textAlign: 'right', marginTop: '10px' }}>
              <button
                type="submit"
                disabled={loading || !question.trim() || !model}
                className="submit-button-aligned"
              >
                Submit
              </button>
            </div>
          </form>
        </div>
        <div className="output-column-single">
          <ModelOutputPanel
            content={answer || DEFAULT_TEXT}
            isLoading={loading}
            isModelRemote={isModelRemote}
            sources={sources}
            isStreaming={streaming}
          />
          <div className="model-controls-bottom">
            <select
              className="model-dropdown"
              value={model}
              onChange={(e) => handleModelSelection(e.target.value)}
            >
              <option value="">Select Model</option>
              {Object.entries(availableModels).map(([id, { description }]) => (
                <option key={id} value={id}>{description}</option>
              ))}
              {Object.values(anthropicModels).map((m) => (
                <option key={m.id} value={m.id}>{m.displayName} (your API key)</option>
              ))}
              {isLoggedIn && !anthropicApiKey && (
                <option value={ADD_API_KEY_OPTION}>+ Add your API key</option>
              )}
            </select>
            <label style={{ marginLeft: '10px', whiteSpace: 'nowrap' }}>
              <input
                type="checkbox"
                checked={useRag}
                disabled={!model || !isLoggedIn}
                onChange={(e) => setUseRag(e.target.checked)}
                style={{ marginRight: '5px' }}
              />
              Use RAG
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}

function filterCitedSources(sources: Source[], answer: string, returnAll: boolean): Source[] {
  if (returnAll) return sources;
  return sources.filter((doc) => answer.includes(doc.doc_id));
}

/**
 * Defense in depth: scrub any `sk-ant-*` substrings out of errors before they
 * hit the console. Our own code never puts the key into errors, but third
 * parties may.
 */
function sanitizeForLogging(err: unknown): unknown {
  if (!(err instanceof Error)) return err;
  const redacted = err.message.replace(/sk-ant-[A-Za-z0-9_-]+/g, 'sk-ant-***');
  return redacted === err.message ? err : new Error(redacted);
}

export default App;
